import { useContext } from 'react'
import { Pressable, Text, View } from 'react-native'
import { useMutation, useQuery } from '@apollo/client'
import { useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'

import { DELETE_PAYMENT_METHOD, UPDATE_PAYMENT_METHOD } from '@/constants/graphql/mutations'
import { GET_USER_PAYMENT_METHODS } from '@/constants/graphql/queries'
import { fundsPinErrorMessage } from '@/utils/fundsPinError'
import { useRouter } from '@/utils/navigation'
import { getUploadPaymentInput, groupPaymentMethodsKeys } from '@/utils/payment'
import {
  Button,
  FormScrollView,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  useModal,
  useToast
} from '@/components/base'
import { PaymentMethodForm, type PaymentMethodValues } from '@/components/form'
import { Stack } from '@/components/layout'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import LoadingScreen from '@/components/LoadingScreen'
import NavigationBar from '@/components/NavigationBar'
import NotFoundScreen from '@/components/NotFoundScreen'
import { SecurityVerificationContext, SecurityVerificationTrigger } from '@/components/SecurityVerificationScreen'
import { SelectPaymentMethodContext } from '@/components/SelectPaymentMethodScreen'
import ServerErrorScreen from '@/components/ServerErrorScreen'

const PAYMENT_METHOD_KIND = ['bank', 'alipay', 'wechat', 'ermb', 'qq']

export default function PaymentMethodDetailsScreen() {
  const router = useRouter()
  const { t } = useTranslation()
  const { openModal, closeModal } = useModal()
  const { close: closeSecurityVerification, setCb, open } = useContext(SecurityVerificationContext)
  const {
    selectedBuyPayment,
    setSelectedBuyPayment,
    setSelectedBuyPaymentText,
    selectedSellPayment,
    setSelectedSellPayment
  } = useContext(SelectPaymentMethodContext)
  const toast = useToast()
  const loading = useLoadingIndicator()
  const id = useLocalSearchParams().id as string

  const { data, loading: isLoading, refetch } = useQuery(GET_USER_PAYMENT_METHODS)
  const paymentMethodsKeys = groupPaymentMethodsKeys(data)

  const [deletePaymentMethod] = useMutation(DELETE_PAYMENT_METHOD, {
    onCompleted: () => {
      loading.stop()
      router.back()
      closeSecurityVerification()
      toast.show({ variant: 'success', description: t('paymentMethod:deleted') })

      if (selectedBuyPayment?.id === id) {
        setSelectedBuyPayment(undefined)
        setSelectedBuyPaymentText('')
      }
      const key = Object.values(selectedSellPayment)
        .find((payment) => payment.id === id)
        ?.kind.toString()

      if (key) {
        const nextSelectedSellPayment: Record<string, any> = {}
        for (const [objKey, objValue] of Object.entries(selectedSellPayment)) {
          if (objKey === key) {
            continue
          }
          nextSelectedSellPayment[objKey] = { ...objValue }
        }

        setSelectedSellPayment(nextSelectedSellPayment)
      }
    },
    onError: (error) => {
      loading.stop()
      const pinErrorMessage = fundsPinErrorMessage(error)
      if (pinErrorMessage) {
        toast.show({ variant: 'warning', description: pinErrorMessage })
      } else {
        toast.show({ variant: 'warning', description: t('paymentMethod:deleteFailed') })
      }
    },
    refetchQueries: [GET_USER_PAYMENT_METHODS]
  })

  const [updatePaymentMethod] = useMutation(UPDATE_PAYMENT_METHOD, {
    onCompleted: () => {
      loading.stop()
      router.back()
      closeSecurityVerification()
      toast.show({ variant: 'success', description: t('paymentMethod:updated') })

      if (selectedBuyPayment?.id === id) {
        setSelectedBuyPayment(undefined)
        setSelectedBuyPaymentText('')
      }
    },
    onError: (error) => {
      loading.stop()
      const pinErrorMessage = fundsPinErrorMessage(error)
      if (pinErrorMessage) {
        toast.show({ variant: 'warning', description: pinErrorMessage })
      } else {
        toast.show({ variant: 'warning', description: t('paymentMethod:updateFailed') })
      }
    },
    refetchQueries: [GET_USER_PAYMENT_METHODS]
  })

  function onSubmit(formValue: PaymentMethodValues, withdrawalPassword: string, kind: string, version?: number) {
    if (version === undefined) {
      toast.show({ variant: 'warning', description: t('paymentMethod:updateFailed') })
      return
    }
    loading.start()
    // console.log({ ...formValue, withdrawalPassword, kind })
    updatePaymentMethod({
      variables: {
        paymentMethodInput: getUploadPaymentInput(formValue, kind),
        withdrawalPassword,
        currentVersion: version.toString(),
        paymentMethodId: id
      }
    })
  }

  function onDelete(withdrawalPassword: string) {
    loading.start()

    // console.log({ id, withdrawalPassword })

    deletePaymentMethod({
      variables: {
        paymentMethodId: id,
        withdrawalPassword
      }
    })
  }

  function handleOpenModal(isLastOne: boolean) {
    openModal({
      children: (
        <>
          <ModalHeader className={'p-4'}>
            <ModalTitle>{t('paymentMethod:confirmDelete')}</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <Text
              className={'text-center text-sm text-text-secondary'}
            >{t('paymentMethod:confirmDeleteDescription', {
              lastOne: isLastOne ? t('paymentMethod:lastPaymentMethod') : ''
            })}</Text>
          </ModalBody>
          <ModalFooter>
            <Button className={'flex-1'} variant={'outline'} size={'md'} onPress={closeModal}>
              {t('cancel')}
            </Button>
            <SecurityVerificationTrigger
              className={'flex-1'}
              onPress={() => {
                closeModal()
                setCb(() => onDelete)
                open()
              }}
            >
              {t('paymentMethod:delete')}
            </SecurityVerificationTrigger>
          </ModalFooter>
        </>
      ),
      onClose: closeModal
    })
  }

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!data) {
    return <ServerErrorScreen onPress={refetch} />
  }

  const paymentMethod = data.getUserPaymentMethods.find((method: Record<string, any>) => method.id === id)
  if (!paymentMethod) {
    return <NotFoundScreen />
  }
  const kind = PAYMENT_METHOD_KIND[paymentMethod.kind]
  const paymentMethodInfo = paymentMethod.info[kind]
  const { name = '', bankName = '', branchName = '', account = '', url = '' } = paymentMethodInfo

  return (
    <>
      <Stack.Screen
        options={{
          header: () => (
            <NavigationBar
              rightElement={
                <Pressable className={'p-2'} onPress={() => handleOpenModal(data.getUserPaymentMethods.length === 1)}>
                  <Text className={'text-base font-medium text-text-tertiary'}>{t('paymentMethod:delete')}</Text>
                </Pressable>
              }
            >
              {t(`payment:${kind}`)}
            </NavigationBar>
          )
        }}
      />
      <FormScrollView className={'flex-1'} contentContainerStyle={{ flexGrow: 1 }}>
        <View className={'grow bg-background-secondary p-4'}>
          <PaymentMethodForm
            version={paymentMethod.version}
            kind={kind}
            onSubmit={onSubmit}
            paymentMethodsKeys={paymentMethodsKeys}
            defaultValues={{
              name,
              bankName,
              branchName,
              account,
              url
            }}
          />
          <View className={'h-4'} />
        </View>
      </FormScrollView>
    </>
  )
}
